import React, { Fragment } from "react";
import { AppBar, IconButton, Link, Toolbar, Typography, makeStyles } from "@material-ui/core";
import LibraryAwareLogoV2 from '../../../../components/LibraryAwareLogo/LibraryAwareLogoV2';
import * as Constants from "../../../../data/StringConstants";
import ProfileMenu from "../../../../components/Menus/ProfileMenu/ProfileMenu";
import OrgSelectPopover from './OrgPicker/OrgSelectPopover';
import { orgRootURL } from "../../../../utils/urlHelpers";
import { Permissions } from '../../../../data/Permissions';
import CustomSvgIcon from "../../../../components/CustomSvgIcon/CustomSvgIcon";

const useStyles = makeStyles(theme => ({
	root: {
		flexGrow: 1,
        borderBottomColor: theme.palette.brandingV2.HeaderBorderColor,
        borderBottomWidth: 1,
	},
	appBar: {
		background: theme.palette.brandingV2.HeaderBackgroundColor
	},
	toolbar: {
        paddingLeft: theme.spacing(6),
        paddingRight: theme.spacing(6)
	},
    navigation: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
		flexGrow: 1
    },
    tools: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
    },
	link: {
        margin: theme.spacing(0, 3),
        color: theme.palette.brandingV2.DarkFontColor,
        fontWeight: 500,
        "&:focus,&:hover": { 
            color: theme.palette.brandingV2.DarkFontColor,
        }
    },
    IconBtn: {
        "&:focus,&:hover": { 
            color: theme.palette.brandingV2.DarkFontColor,
            backgroundColor: `#D5D5D5`,
        } 
    },
	logo: {
		height: 30,
		marginTop: 10,
        marginBottom: 10,
        marginRight: 10
	},
    avatarWrapper : {
        margin: "12px 12px 12px 10px"
    }
}));

// If the User is a Publisher, they are only allowed to view the "Templates" section in the 
// Admin dashboard. Due to the way the breadcrumbs are rendered in the LA.Views.AdminDashboard of application.js,
// the default case in the breadcrumb switch is to send the user to "Basic Settings" which is incorrect.
// We need to set the href to "admin/templates" in order for the proper breadcrumb to render, which also determines
// the backend endpoint to be called.
const getAdminUrl = (orgRoot, userPermissions) => {
    let isPublisher = userPermissions[Permissions.publisher] && !userPermissions[Permissions.admin];

    return isPublisher 
        ? `${orgRoot}/${Constants.ROUTE_URIS.ADMIN_FOR_PUBLISHERS}`
        : `${orgRoot}/${Constants.ROUTE_URIS.ADMIN}`
}

export default function NavBarMain(props) {
    const classes = useStyles();
	const { userPermissions, homeViewEnabled, errorPage } = props; 

    // We need to construct the complete URI for the menu items since the ProfileMenu can exist 
    // in parts of the app (ex: Legacy Editor) which are not running the Backbone application.
    const orgRoot = orgRootURL();
    const homeUrl = `${orgRoot}/${Constants.ROUTE_URIS.HOME}`;
    const foldersUrl = `${orgRoot}/${Constants.ROUTE_URIS.FOLDERS_ALL_FOLDERS}`;
    const newslettersUrl = `${orgRoot}/${Constants.ROUTE_URIS.NEWSLETTERS}`;
    const subscribersUrl = `${orgRoot}/${Constants.ROUTE_URIS.SUBSCRIBERS}`;
    const reportsUrl = `${orgRoot}/${Constants.ROUTE_URIS.REPORTS}`;
    const adminUrl = getAdminUrl(orgRoot, userPermissions);

    const renderLinks = () => (
        <Fragment>
            {(homeViewEnabled && userPermissions[Permissions.editor]) && (
                <Link
                    variant="button"
                    color="inherit"
                    href={homeUrl}
                    data-route-val={Constants.NAV_LINK_HOME.toLowerCase()}
                    className={classes.link}
                >
                    {Constants.NAV_LINK_HOME}
                </Link>
            )}
            <Link
                variant="button"
                color="inherit"
                href={foldersUrl}
                data-route-val={Constants.NAV_LINK_FOLDERS.toLowerCase()}
                className={classes.link}
            >
                {Constants.NAV_LINK_FOLDERS}
            </Link>
            <Link
                variant="button"
                color="inherit"
                href={newslettersUrl}
                data-route-val={Constants.NAV_LINK_NEWSLETTERS.toLowerCase()}
                className={classes.link}
            >
                {Constants.NAV_LINK_NEWSLETTERS}
            </Link>
            {userPermissions[Permissions.editor] && (
                <Link
                    variant="button"
                    color="inherit"
                    href={subscribersUrl}
                    data-route-val={Constants.NAV_LINK_SUBSCRIBERS.toLowerCase()}
                    className={classes.link}
                >
                    {Constants.NAV_LINK_SUBSCRIBERS}
                </Link>
            )}
            <Link
                variant="button"
                color="inherit"
                href={reportsUrl}
                data-route-val={Constants.NAV_LINK_REPORTS.toLowerCase()}
                className={classes.link}
            >
                {Constants.NAV_LINK_REPORTS}
            </Link>
            {userPermissions[Permissions.publisher] && (
                <Link
                    variant="button"
                    color="inherit"
                    href={adminUrl}
                    data-route-val={Constants.NAV_LINK_ADMIN.toLowerCase()}
                    className={classes.link}
                >
                    {Constants.NAV_LINK_ADMIN}
                </Link>
            )}
        </Fragment>
    );

	return (
		<div className={classes.root}>
			<AppBar className={classes.appBar}>
                <Toolbar className={classes.toolbar}>
                    <div className={classes.navigation}>
                        <LibraryAwareLogoV2 className={classes.logo}/>
                        {/* When a user gets redirected to an error page (any of the views returned 
                            from HomeController) the orgId is no longer valid and therefore the nav 
                            links are not functional. The desired behavior is to just not render them. 
                            This matches the behavior from the legacy nav bar 
                        */}
						{!errorPage && renderLinks()}
                    </div>
                    <div className={classes.tools}>
                        <OrgSelectPopover {...props} />
                        <IconButton
                            title={Constants.SITE_CONTROLS.WHATS_NEW_TITLE}
                            href={Constants.ROUTE_URIS.NEW}
                            className={classes.IconBtn}
							target="_blank"
                            aria-label={Constants.NAV_LINK_INFO}
                            >
                            <CustomSvgIcon url={Constants.NAV_MENU_CUSTOM_ICONS.INFO} />
                        </IconButton>
                        <IconButton
                            title={Constants.SITE_CONTROLS.HELP_TITLE}
                            href={Constants.ROUTE_URIS.HELP}
                            target="_blank"
                            aria-label={Constants.NAV_LINK_HELP}
                            className={classes.IconBtn}
                         >
                            <CustomSvgIcon url={Constants.NAV_MENU_CUSTOM_ICONS.HELP} />
                        </IconButton>
                        <div className={classes.avatarWrapper}>
                            <ProfileMenu {...props} /> 
                        </div>
                    </div>
                </Toolbar>
			</AppBar>
		</div>
	);
}
